import accordionStyles from "./Accordion.module.css";
import actionIconStyles from "./ActionIcon.module.css";
import alertStyles from "./Alert.module.css";
import appShellStyles from "./AppShell.module.css";
import badgeStyles from "./Badge.module.css";
import breadcrumbsStyles from "./Breadcrumbs.module.css";
import dropzoneStyles from "./Dropzone.module.css";
import inputStyles from "./Input.module.css";
import menuStyles from "./Menu.module.css";
import modalStyles from "./Modal.module.css";
import paperStyles from "./Paper.module.css";
import spotlightStyles from "./Spotlight.module.css";
import tabsStyles from "./Tabs.module.css";
import textStyles from "./Text.module.css";
import textareaStyles from "./Textarea.module.css";
import timelineStyles from "./Timeline.module.css";
import titleStyles from "./Title.module.css";
import type { MantineRadius } from "@mantine/core";
import {
  Accordion,
  ActionIcon,
  Alert,
  AppShell,
  Autocomplete,
  Badge,
  Breadcrumbs,
  Button,
  Card,
  Divider,
  Drawer,
  HoverCard,
  Input,
  LoadingOverlay,
  Menu,
  Modal,
  MultiSelect,
  NumberFormatter,
  NumberInput,
  Overlay,
  Paper,
  Pill,
  Popover,
  Select,
  Tabs,
  Text,
  TextInput,
  Textarea,
  Timeline,
  Title,
  Tooltip,
  createTheme
} from "@mantine/core";
import { Dropzone } from "@mantine/dropzone";
import { Spotlight } from "@mantine/spotlight";

/**
 * https://mantine.dev/theming/theme-object/
 */

const radius: MantineRadius = "sm";
const blur = 2;

export default createTheme({
  breakpoints: {
    xs: "30em",
    sm: "48em",
    md: "64em",
    lg: "74em",
    xl: "120em"
  },
  fontFamily: "Inter, system-ui, Avenir, Helvetica, Arial, sans-serif",
  fontSmoothing: true,
  black: "#425466",
  white: "#fff",
  activeClassName: "active",
  primaryColor: "blue",
  primaryShade: { light: 6, dark: 6 },
  defaultRadius: "sm",
  cursorType: "pointer",
  colors: {
    red: ["#fef2f2", "#fee2e2", "#fecaca", "#fca5a5", "#f87171", "#ef4444", "#dc2626", "#b91c1c", "#991b1b", "#7f1d1d"],
    pink: [
      "#fdf2f8",
      "#fce7f3",
      "#fbcfe8",
      "#f9a8d4",
      "#f472b6",
      "#ec4899",
      "#db2777",
      "#be185d",
      "#9d174d",
      "#831843"
    ],
    grape: [
      "#faf5ff",
      "#f3e8ff",
      "#e9d5ff",
      "#d8b4fe",
      "#c084fc",
      "#a855f7",
      "#9333ea",
      "#7e22ce",
      "#6b21a8",
      "#581c87"
    ],
    violet: [
      "#f5f3ff",
      "#ede9fe",
      "#ddd6fe",
      "#c4b5fd",
      "#a78bfa",
      "#8b5cf6",
      "#7c3aed",
      "#6d28d9",
      "#5b21b6",
      "#4c1d95"
    ],
    indigo: [
      "#eef2ff",
      "#e0e7ff",
      "#c7d2fe",
      "#a5b4fc",
      "#818cf8",
      "#6366f1",
      "#4f46e5",
      "#4338ca",
      "#3730a3",
      "#312e81"
    ],
    cyan: [
      "#ecfeff",
      "#cffafe",
      "#a5f3fc",
      "#67e8f9",
      "#22d3ee",
      "#06b6d4",
      "#0891b2",
      "#0e7490",
      "#155e75",
      "#164e63"
    ],
    teal: [
      "#f0fdfa",
      "#ccfbf1",
      "#99f6e4",
      "#5eead4",
      "#2dd4bf",
      "#14b8a6",
      "#0d9488",
      "#0f766e",
      "#115e59",
      "#134e4a"
    ],
    green: [
      "#f0fdf4",
      "#dcfce7",
      "#bbf7d0",
      "#86efac",
      "#4ade80",
      "#22c55e",
      "#16a34a",
      "#15803d",
      "#166534",
      "#14532d"
    ],
    lime: [
      "#f7fee7",
      "#ecfccb",
      "#d9f99d",
      "#bef264",
      "#a3e635",
      "#84cc16",
      "#65a30d",
      "#4d7c0f",
      "#3f6212",
      "#365314"
    ],
    orange: [
      "#fff7ed",
      "#ffedd5",
      "#fed7aa",
      "#fdba74",
      "#fb923c",
      "#f97316",
      "#ea580c",
      "#c2410c",
      "#9a3412",
      "#7c2d12"
    ]
  },
  components: {
    Accordion: Accordion.extend({
      classNames: {
        item: accordionStyles.item,
        label: accordionStyles.label,
        panel: accordionStyles.panel,
        chevron: accordionStyles.chevron
      }
    }),
    Text: Text.extend({
      classNames: {
        root: textStyles.root
      }
    }),
    Timeline: Timeline.extend({
      classNames: {
        itemBullet: timelineStyles.itemBullet
      }
    }),
    Tooltip: Tooltip.extend({
      defaultProps: {
        openDelay: 250,
        offset: 10,
        withArrow: true
      }
    }),
    MultiSelect: MultiSelect.extend({
      defaultProps: {
        comboboxProps: { shadow: "xs", offset: 0 }
      }
    }),
    Autocomplete: Autocomplete.extend({
      defaultProps: {
        comboboxProps: { shadow: "md", offset: 0 }
      }
    }),
    Input: Input.extend({
      classNames: {
        wrapper: inputStyles.wrapper,
        input: inputStyles.input
      }
    }),
    TextInput: TextInput.extend({
      defaultProps: {
        autoCorrect: "off",
        autoComplete: "off",
        spellCheck: "false"
      }
    }),
    NumberInput: NumberInput.extend({
      defaultProps: {
        hideControls: true,
        // it seems like sending more than 16 decimals makes the algorithm break partially
        decimalScale: 16,
        /**
         * Values below are set to avoid this issue: https://github.com/orgs/mantinedev/discussions/5421
         * (avoid conversion to string, which is not authorized by the api)
         */
        max: Number.MAX_SAFE_INTEGER,
        min: Number.MIN_SAFE_INTEGER
      }
    }),
    Textarea: Textarea.extend({
      classNames: {
        input: textareaStyles.input
      },
      defaultProps: {
        autoCorrect: "off",
        autoComplete: "off",
        spellCheck: "false"
      }
    }),
    Card: Card.extend({
      defaultProps: {
        shadow: "xs",
        radius,
        py: "xs",
        px: "xs"
      }
    }),
    Pill: Pill.extend({
      defaultProps: {
        radius
      }
    }),
    Paper: Paper.extend({
      defaultProps: {
        radius,
        p: "lg",
        shadow: "xs",
        withBorder: true
      },
      classNames: {
        root: paperStyles.root
      }
    }),
    Breadcrumbs: Breadcrumbs.extend({
      defaultProps: {
        separator: "/",
        classNames: {
          root: breadcrumbsStyles.root,
          breadcrumb: breadcrumbsStyles.breadcrumb,
          separator: breadcrumbsStyles.separator
        }
      }
    }),
    Badge: Badge.extend({
      defaultProps: {
        radius: "sm"
      },
      classNames: {
        label: badgeStyles.label
      }
    }),
    Menu: Menu.extend({
      defaultProps: {
        shadow: "sm",
        offset: 4,
        withArrow: true
      },
      classNames: {
        item: menuStyles.item,
        itemSection: menuStyles.itemSection
      }
    }),
    Popover: Popover.extend({
      defaultProps: {
        shadow: "lg",
        offset: 4,
        position: "bottom-start"
      }
    }),
    HoverCard: HoverCard.extend({
      defaultProps: {
        shadow: "md",
        withArrow: true,
        position: "bottom",
        transitionProps: { transition: "pop" },
        openDelay: 250
      }
    }),
    Button: Button.extend({
      defaultProps: {
        miw: 100
      }
    }),
    Drawer: Drawer.extend({
      defaultProps: {
        padding: 0,
        returnFocus: true
      }
    }),
    Modal: Modal.extend({
      defaultProps: {
        returnFocus: true,
        closeOnClickOutside: false
      },
      classNames: {
        content: modalStyles.content,
        header: modalStyles.header,
        title: modalStyles.title,
        body: modalStyles.body
      }
    }),
    Overlay: Overlay.extend({
      defaultProps: {
        backgroundOpacity: 0.35,
        color: "#aaa",
        blur
      }
    }),
    LoadingOverlay: LoadingOverlay.extend({
      defaultProps: {
        overlayProps: {
          blur,
          bg: "transparent"
        }
      }
    }),
    Select: Select.extend({
      defaultProps: {
        checkIconPosition: "right"
      }
    }),
    ActionIcon: ActionIcon.extend({
      defaultProps: {
        variant: "subtle",
        color: "gray"
      },
      classNames: {
        root: actionIconStyles.root
      }
    }),
    Title: Title.extend({
      classNames: {
        root: titleStyles.root
      },
      defaultProps: {
        fw: 800
      }
    }),
    Alert: Alert.extend({
      classNames: {
        icon: alertStyles.icon
      }
    }),
    NumberFormatter: NumberFormatter.extend({
      defaultProps: {
        style: {
          fontSize: "1rem"
        }
      }
    }),
    Divider: Divider.extend({
      defaultProps: {
        color: "var(--mantine-color-default-border)"
      }
    }),
    Tabs: Tabs.extend({
      classNames: {
        root: tabsStyles.root,
        tab: tabsStyles.tab,
        panel: tabsStyles.panel
      }
    }),
    AppShell: AppShell.extend({
      classNames: {
        header: appShellStyles.header
      }
    }),
    Spotlight: Spotlight.extend({
      classNames: {
        content: spotlightStyles.content,
        actionsGroup: spotlightStyles.actionsGroup,
        actionSection: spotlightStyles.actionSection,
        action: spotlightStyles.action,
        actionBody: spotlightStyles.actionBody,
        actionLabel: spotlightStyles.actionLabel
      }
    }),
    Dropzone: Dropzone.extend({
      classNames: {
        root: dropzoneStyles.root
      }
    })
  }
});
